import {BADGE_MEMBER_STATUSES, type BadgeMemberStatus} from '@/consts/loyalty/badgeMemberStatuses';
import {JOURNEY_STATUS_TYPES} from '@/consts/loyalty/journeyStatusTypes';
import type {BadgeImageItem, Journey, JourneyBadge, JourneyLevel} from '@/types/loyalty';

export const isJourneyCompleted = (journey?: Journey): boolean =>
    journey?.memberJourneyStatus === JOURNEY_STATUS_TYPES.COMPLETED;

export const isJourneyInProgress = (journey?: Journey): boolean =>
    journey?.memberJourneyStatus === JOURNEY_STATUS_TYPES.IN_PROGRESS;

export const isJourneyLevelInProgress = ({actionsLeft, memberJourneyActions}: JourneyLevel): boolean => {
    const total = memberJourneyActions?.length ?? 0;
    return typeof actionsLeft === 'number' && actionsLeft > 0 && actionsLeft <= total;
};

export const isJourneyLevelCompleted = ({actionsLeft}: JourneyLevel): boolean => actionsLeft === 0;

/** Maps a journey's status onto the `viewType` used to pick the badge image. */
export const getJourneyBadgeViewType = (journey?: Journey): BadgeMemberStatus => {
    const status = journey?.memberJourneyStatus;
    if (status === JOURNEY_STATUS_TYPES.COMPLETED) return BADGE_MEMBER_STATUSES.ACHIEVED;
    if (status === JOURNEY_STATUS_TYPES.IN_PROGRESS) return BADGE_MEMBER_STATUSES.IN_PROGRESS;
    return BADGE_MEMBER_STATUSES.LOCKED;
};

export const getBadgeImageByViewType = (
    badge: JourneyBadge | null | undefined,
    viewType: BadgeMemberStatus
): BadgeImageItem | undefined => badge?.badgeImage?.find((img) => img?.viewType === viewType);

/** Appends AEM image-resize query params to a raw badge image URL. */
export const formatBadgeImageUrl = (imageURL?: string | null, width = 256, height = 256): string | null => {
    if (!imageURL) return null;
    return `${imageURL.trim()}?width=${width}&height=${height}`;
};

type JourneyInfo = {
    actionsLeft: number;
    actionsCount: number;
    currentLevel: JourneyLevel;
    currentLevelBadge: JourneyBadge | null;
    progressCompleted: number;
    progressTotal: number;
    isSingleLevel: boolean;
};

/**
 * Resolves the single badge to display for a journey (even multi-level) and
 * aggregates progress totals. Mirrors the legacy UWM `getJourneyInfo`.
 */
export const getJourneyInfo = (journey?: Journey): JourneyInfo => {
    const levels = journey?.journeyLevels ?? [];
    const isMemberJourney = Boolean(journey?.memberJourneyStatus);

    let actionsLeft = 0;
    let actionsCount = 0;
    let currentLevel: JourneyLevel | null = null;
    let completedLevels = 0;

    for (const level of levels) {
        const actions = isMemberJourney ? level.memberJourneyActions : level.journeyActions;
        const levelActionsCount = actions?.length ?? 0;

        actionsCount += levelActionsCount;
        actionsLeft +=
            typeof level.actionsLeft === 'number' && !Number.isNaN(level.actionsLeft)
                ? level.actionsLeft
                : levelActionsCount;

        if (isJourneyLevelCompleted(level)) {
            completedLevels += 1;
            continue;
        }
        if (!isJourneyLevelInProgress(level)) continue;
        currentLevel ??= level;
    }

    // Fall back: last level for member journeys, first level otherwise.
    currentLevel ??= levels[isMemberJourney ? levels.length - 1 : 0] ?? ({} as JourneyLevel);

    const isSingleLevel = levels.length === 1;
    const currentLevelBadge = currentLevel.memberJourneyBadge ?? currentLevel.journeyBadge ?? null;
    const progressCompleted = isSingleLevel ? actionsCount - actionsLeft : completedLevels;
    const progressTotal = isSingleLevel ? actionsCount : levels.length;

    return {
        actionsLeft,
        actionsCount,
        currentLevel,
        currentLevelBadge,
        progressCompleted,
        progressTotal,
        isSingleLevel,
    };
};
